import { useState } from "react";
import styles from "./Servicessection.module.css";
import aiDrivenFamilyDiscoveryImg from "../../assets/img/services/ai-driven-family-discovery.jpg";
import dnaTestingAndMatchingImg from "../../assets/img/services/dna-testing-and-matching.jpg";
import familyTreeBuildImg from "../../assets/img/services/family-tree-build.jpg";
import igitreeConnectImg from "../../assets/img/services/igitree-connect.jpg";

/* ── Types ── */
export interface Service {
  id: number;
  name: string;
  image: string;
  description: string;
  code: string;
}

/* ── Data ── */
const services: Service[] = [
  {
    id: 2,
    name: "AI Driven Family Discovery",
    image: aiDrivenFamilyDiscoveryImg,
    description:
      "Discover your roots with iGiTREE's AI-Powered Family Matching. Our sophisticated AI delves into background questions about your birthplace, birth date, and family members. With each answer, our system becomes more adept at unveiling potential relatives, bridging families, and reconnecting lost ties.",
    code: "ajsdsas",
  },
  {
    id: 4,
    name: "DNA Testing And Matching",
    image: dnaTestingAndMatchingImg,
    description:
      "Revolutionizing family discovery for the digital age. Dive deep into heritage with our DNA Testing and Matching. Using AI-Driven questions tailored to individual backgrounds, we're reuniting families and crafting intricate family trees. With iGiTREE, uncover and share your lineage like never before.",
    code: "ajsloas",
  },
  {
    id: 1,
    name: "Family Tree Building",
    image: familyTreeBuildImg,
    description:
      "iGiTREE invites you to uncover your ancestry with ease and simplicity. We focus on building your family tree, allowing you to reconnect with your heritage and loved ones. Our platform offers user-friendly DNA testing and matching services, empowering you to connect with potential relatives.",
    code: "ajskjas",
  },
  {
    id: 3,
    name: "iGiTREE Connect",
    image: igitreeConnectImg,
    description:
      "Experience seamless communication on iGiTREE's platform. Engage in meaningful conversations — messages or video chats — with potential relatives. We've streamlined the path to forge and strengthen family connections, making it easier than ever.",
    code: "ajknjas",
  },
];

const INITIAL_VISIBLE = 3;

/* Inline SVG fallback when image file is missing */
const FALLBACK =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='220'%3E%3Crect width='400' height='220' fill='%23EEF3FF'/%3E%3Ccircle cx='200' cy='85' r='36' fill='%23c7d7ff'/%3E%3Cellipse cx='200' cy='155' rx='56' ry='32' fill='%23c7d7ff'/%3E%3C/svg%3E";

/* ── Card ── */
function ServiceCard({ service }: { service: Service }) {
  const [imgSrc, setImgSrc] = useState(service.image);

  return (
    <article className={styles.card}>
      <div className={styles.cardImageWrap}>
        <img
          src={imgSrc}
          alt={service.name}
          className={styles.cardImage}
          loading="lazy"
          decoding="async"
          onError={() => setImgSrc(FALLBACK)}
        />
        <div className={styles.cardImageOverlay} />
      </div>

      <div className={styles.cardBody}>
        <h3 className={styles.cardTitle}>{service.name}</h3>
        <p className={styles.cardDesc}>{service.description}</p>
        <button className={styles.readMore}>
          Explore <span className={styles.arrow}>→</span>
        </button>
      </div>

      <div className={styles.cardGlow} />
    </article>
  );
}

/* ── Section ── */
export default function ServicesSection() {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? services : services.slice(0, INITIAL_VISIBLE);

  return (
    <section id="services" className={styles.section}>
      <div className={styles.decoSquare} aria-hidden />
      <div className={styles.decoBlobRight} aria-hidden />
      <div className={styles.decoDots} aria-hidden />
      <div className={styles.decoCircleOutline} aria-hidden />
      <div className={styles.decoRectOutline} aria-hidden />

      <div className={styles.heading}>
        <p className={styles.eyebrow}>Our Core Services</p>
        <h2 className={styles.headingTitle}>
          Reconnecting Families,{" "}
          <em className={styles.headingEm}>One Story at a Time</em>
        </h2>
        <div className={styles.divider}>
          <span className={styles.dividerLong} />
          <span className={styles.dividerShort} />
        </div>
        <p className={styles.headingDesc}>
          iGiTREE harnesses the power of artificial intelligence and DNA science
          to help you discover your heritage, build your family tree, and
          reconnect with loved ones — no matter where in the world they are.
          Start your journey today and let your ancestry tell its story.
        </p>
      </div>

      <div className={styles.grid}>
        {visible.map((service) => (
          <ServiceCard key={service.id} service={service} />
        ))}
      </div>

      {services.length > INITIAL_VISIBLE && (
        <div className={styles.viewMoreWrap}>
          <button
            className={styles.viewMoreBtn}
            onClick={() => setShowAll((prev) => !prev)}
          >
            {showAll
              ? "View Less ↑"
              : `View All Services (${services.length - INITIAL_VISIBLE} more) ↓`}
          </button>
        </div>
      )}
    </section>
  );
}